import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Shield, Check, AlertTriangle, Flame, Gauge, DollarSign, Settings } from 'lucide-react';
import Footer from '../../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

const DPFOffPage = () => {
  useCanonical('https://ecuflashservice.com/services/dpf-off');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>DPF Delete Service | Remove DPF Filter Software | From $80 | Fast Delivery</title>
        <meta name="description" content="Professional DPF delete & removal service. Disable DPF regeneration, remove error codes P2002 P2463. All diesel brands - BMW, Mercedes, VW, Audi, Ford. 24h turnaround." />
        <meta name="keywords" content="DPF delete, DPF removal, DPF off, diesel particulate filter delete, DPF regeneration disable, P2002, P2463" />
        <link rel="canonical" href="https://ecuflashservice.com/services/dpf-off" />
        
        {/* Service Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "DPF Delete Service",
            "description": "Professional Diesel Particulate Filter (DPF) removal and ECU remapping service. Remove DPF restrictions and restore your diesel engine's full performance potential.",
            "image": "https://ecuflashservice.com/favicon-512.png",
            "brand": {
              "@type": "Brand",
              "name": "ECU Flash Service"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://ecuflashservice.com/services/dpf-off",
              "price": "80",
              "priceCurrency": "USD",
              "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "ECU Flash Service",
                "url": "https://ecuflashservice.com"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "bestRating": "5",
              "worstRating": "1",
              "reviewCount": "89"
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/services/egr-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">EGR OFF</Link>
              <Link to="/services/adblue-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">AdBlue OFF</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <Shield className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Most Popular Service</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">DPF Delete</span>{' '}
            <span className="text-[#00F0FF]">& Removal Service</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto mb-8">
            Professional DPF software delete. Remove regeneration cycles, disable DPF monitoring & clear error codes. All diesel vehicles supported.
          </p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Order DPF Delete →</span>
          </Link>
        </div>
      </section>

      {/* What is DPF */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-6">What is DPF Delete?</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                The Diesel Particulate Filter (DPF) is an emissions control device that captures and stores soot particles from your diesel engine&apos;s exhaust. Over time, the DPF requires &quot;regeneration&quot; cycles to burn off accumulated soot.
              </p>
              <p className="text-[#888] leading-relaxed mb-4">
                DPF delete is an ECU software modification that removes all DPF-related functions from your engine management system. This includes disabling regeneration cycles, DPF monitoring, differential pressure sensors, and related error codes.
              </p>
              <p className="text-[#888] leading-relaxed">
                After software modification, the physical DPF can be removed or replaced with a straight pipe by a qualified mechanic, allowing unrestricted exhaust flow.
              </p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-8">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">What We Disable</h3>
              <ul className="space-y-4">
                {[
                  'DPF regeneration cycles',
                  'DPF differential pressure monitoring',
                  'DPF temperature sensors',
                  'Soot mass calculation',
                  'Related DTC error codes'
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#00F0FF]/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#00F0FF]" />
                    </div>
                    <span className="text-[#888]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problems with DPF */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-4">Common DPF Problems</h2>
          <p className="text-[#888] text-center mb-12 max-w-2xl mx-auto">These issues often lead vehicle owners to consider DPF delete</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Flame, title: 'Failed Regeneration', desc: 'Short trips prevent complete regeneration, causing soot buildup and eventual clogging' },
              { icon: AlertTriangle, title: 'Limp Mode', desc: 'Blocked DPF triggers reduced power mode, limiting speed and acceleration' },
              { icon: DollarSign, title: 'Expensive Replacement', desc: 'New DPF units cost $1,000-$5,000+ plus labor for installation' },
              { icon: Gauge, title: 'Increased Fuel Use', desc: 'Active regeneration burns extra fuel, reducing economy by 5-10%' },
              { icon: Settings, title: 'Frequent Repairs', desc: 'Related sensors and components fail, requiring repeated repairs' },
              { icon: Zap, title: 'Performance Loss', desc: 'Back pressure from DPF reduces engine efficiency and power output' },
            ].map((item, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
                <item.icon className="w-8 h-8 text-[#00F0FF] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#666] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Benefits of DPF Delete</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { value: '+15-30%', label: 'More Power', desc: 'Reduced back pressure unleashes hidden power' },
              { value: '5-15%', label: 'Better MPG', desc: 'No regeneration cycles means better economy' },
              { value: '$0', label: 'No DPF Repairs', desc: 'Never pay for DPF replacement again' },
              { value: '∞', label: 'Reliability', desc: 'Eliminate a major failure point' },
            ].map((item, i) => (
              <div key={i} className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 p-6 text-center group hover:border-[#00F0FF]/30 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#00F0FF]/5 rounded-full blur-3xl group-hover:bg-[#00F0FF]/10 transition-all" />
                <div className="text-3xl font-bold text-[#00F0FF] mb-2">{item.value}</div>
                <div className="text-white font-semibold mb-2">{item.label}</div>
                <p className="text-[#666] text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Vehicles */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Supported Vehicles</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Volkswagen / Audi', 'BMW', 'Mercedes-Benz', 'Ford',
              'Toyota / Lexus', 'Mazda', 'Hyundai / Kia', 'Nissan',
              'Peugeot / Citroën', 'Renault', 'Fiat', 'Volvo',
              'Land Rover', 'Jeep', 'Chinese Trucks', 'All Diesels'
            ].map((brand, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-white/5 p-4 text-center text-[#888] font-medium hover:border-[#00F0FF]/30 hover:text-white transition-all">
                {brand}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-8 h-8 text-[#FF003C] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#FF003C] mb-3">Legal Notice</h3>
                <p className="text-[#FF003C]/80 mb-4">
                  DPF delete is intended <strong>ONLY for off-road, racing, and competition vehicles</strong> in jurisdictions where such modifications are permitted. Removing or tampering with emission control devices on road-registered vehicles may violate local, state, or federal laws.
                </p>
                <p className="text-[#FF003C]/60 text-sm">
                  By using this service, you confirm that the modified file will be used in compliance with all applicable laws in your jurisdiction. ECU Flash Service is not responsible for any misuse of modified files.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready for DPF-Free Performance?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and get your DPF delete solution within 20-60 minutes</p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Start Now →</span>
          </Link>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default DPFOffPage;
